import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router-dom'
import { Icon } from '../components/ui/Icon'
import { actionDescription } from '../lib/display-labels'
import { useTenant } from '../tenancy/TenantProvider'
import { getMyActions, type MyAction } from './governance-api'

function displayDate(value: string | null) {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
}

function ActionRow({ action, waiting = false, onSelect }: { action: MyAction; waiting?: boolean; onSelect?: () => void }) {
  return <Link to={action.deepLinkPath} className={`governance-my-task${waiting ? ' waiting' : ''}`} onClick={onSelect}>
    <span className="governance-my-task-icon" aria-hidden="true">
      <Icon name={action.sourceType === 'approval_requirement' ? 'scale' : action.sourceType === 'information_request' ? 'document' : 'check'} size={18} />
    </span>
    <span>
      <strong>{action.actionType} — {action.aiUseName}</strong>
      <small>{action.requestedBy ? `Requested by ${action.requestedBy}` : 'Governed action'}{displayDate(action.relevantDate) ? ` · ${displayDate(action.relevantDate)}` : ''}</small>
      <em>{actionDescription(action.description)}</em>
    </span>
    <span className="governance-action-label">{action.primaryAction}</span>
    <Icon name="arrow-right" size={17} aria-hidden="true" />
  </Link>
}

export function MyGovernanceTasks({ alwaysShow = false }: { alwaysShow?: boolean } = {}) {
  const { current } = useTenant()
  const organisationId = current?.organisation.id ?? ''
  const actionsQuery = useQuery({
    queryKey: ['r1-my-actions', organisationId],
    queryFn: () => getMyActions(organisationId),
    enabled: Boolean(organisationId),
  })

  if (!current || actionsQuery.isLoading) return null
  const actions = actionsQuery.data?.actions ?? []
  const waiting = actionsQuery.data?.waitingOnOthers ?? []
  const topActions = actions.slice(0, 5)
  if (!alwaysShow && actions.length === 0 && waiting.length === 0) return null

  return (
    <section className="panel governance-my-tasks" aria-labelledby="my-actions-overview-heading">
      <div className="section-heading compact">
        <div>
          <p className="eyebrow">Your work</p>
          <h2 id="my-actions-overview-heading">Your actions{actions.length ? ` · ${actions.length}` : ''}</h2>
          <p>Formal governance work that currently requires your action.</p>
        </div>
        <Link className="text-link" to="/actions">View all actions →</Link>
      </div>
      {topActions.length ? <div className="governance-my-task-list">{topActions.map((action) => <ActionRow key={`${action.sourceType}-${action.sourceId}`} action={action} />)}</div>
        : <p className="muted">Nothing currently requires your action.</p>}
      {actions.length > topActions.length ? <p className="muted">{actions.length - topActions.length} more {actions.length - topActions.length === 1 ? 'action is' : 'actions are'} available in My actions.</p> : null}
      {waiting.length ? <details className="governance-waiting-list">
        <summary>Waiting on others · {waiting.length}</summary>
        <p>These confirmations are assigned to you but are blocked until requested information is supplied.</p>
        <div className="governance-my-task-list">{waiting.map((action) => <ActionRow key={`waiting-${action.sourceId}`} action={action} waiting />)}</div>
      </details> : null}
    </section>
  )
}

export function MyActionsShellControl() {
  const { current } = useTenant()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const organisationId = current?.organisation.id ?? ''
  const actionsQuery = useQuery({
    queryKey: ['r1-my-actions', organisationId],
    queryFn: () => getMyActions(organisationId),
    enabled: Boolean(organisationId),
    refetchInterval: 60_000,
  })
  const actions = actionsQuery.data?.actions ?? []
  const waiting = actionsQuery.data?.waitingOnOthers ?? []

  useEffect(() => {
    setOpen(false)
  }, [location.pathname, location.search])

  if (!current) return null
  return <details className="my-actions-shell" open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
    <summary aria-label={`Requires action now${actions.length ? `, ${actions.length} ${actions.length === 1 ? 'item' : 'items'}` : ', none'}`}>
      <Icon name="work" size={17} />
      <span>Requires action</span>
      {actions.length ? <strong aria-hidden="true">{actions.length}</strong> : null}
    </summary>
    <div className="my-actions-popover" role="region" aria-label="My actions">
      <header><div><h2>Requires action</h2><p>{actions.length ? `${actions.length} ${actions.length === 1 ? 'item requires' : 'items require'} your action now.` : 'Nothing currently requires your action.'}</p></div><Link className="text-link" to="/actions" onClick={() => setOpen(false)}>View all</Link></header>
      {actionsQuery.isError ? <p role="alert">My actions could not be loaded. Try again shortly.</p> : null}
      {actions.map((action) => <ActionRow key={`shell-${action.sourceType}-${action.sourceId}`} action={action} onSelect={() => setOpen(false)} />)}
      {waiting.length ? <details className="governance-waiting-list"><summary>Waiting on others · {waiting.length}</summary>
        <div className="governance-my-task-list">{waiting.map((action) => <ActionRow key={`shell-waiting-${action.sourceId}`} action={action} waiting onSelect={() => setOpen(false)} />)}</div>
      </details> : null}
    </div>
  </details>
}
